import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.js';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  COUNTERPARTY_CACHE_DATE_FORMAT,
  counterpartySearchInputProps,
  counterpartySelectorPaperStyle,
  counterpartySelectorText,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/constants.ts';
import type { TCounterpartySearchCachePayload } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/types.ts';
import { useUserAuthToken } from '~/modules/covenants/hooks/useUserAuthToken.ts';
import type { TApiContragent } from '~/modules/covenants/types.ts';
import { AppDispatch, RootState } from '~/store/store.ts';
import { selectGuaranteesCreateApplicationStatus } from '~/store/guarantees/selectors.ts';
import { createGuaranteesApplication } from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import {
  fetchCallCenterOperatorContragents,
  fetchMyContragents,
} from '~/store/covenants/thunks/contragentThunks.ts';
import useSelectorForm, { IGuaranteesSearchFormValues } from './hooks/useSelectorForm.ts';
import {
  buildGuaranteesCachedSearchOptions,
  buildGuaranteesCacheStorageKey,
  buildGuaranteesApplicationPath,
  buildGuaranteesCounterpartyPath,
  getGuaranteesErrorMessage,
  getCachedGuaranteesSearch,
  isGuaranteesAdministratorPath,
  normalizeGuaranteesSearchCode,
  saveGuaranteesSearchToCache,
} from '../../utils.ts';
import { styles } from './styles.ts';

const GuaranteesCounterpartySelector: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useUserAuthToken();
  const userName = useSelector((state: RootState) => state.users.user.username);
  const createApplicationStatus = useSelector(selectGuaranteesCreateApplicationStatus);
  const [contragents, setContragents] = useState<TApiContragent[]>([]);
  const [isContragentsModalOpen, setIsContragentsModalOpen] = useState(false);
  const [isModalAlertOpen, setIsModalAlertOpen] = useState(false);
  const [modalAlertText, setModalAlertText] = useState('');
  const [cachedSearch, setCachedSearch] = useState<TCounterpartySearchCachePayload | null>(null);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [searchCodeInputValue, setSearchCodeInputValue] = useState('');

  const formMethods = useSelectorForm();
  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { isSubmitting },
  } = formMethods;
  const isAdministratorFlow = isGuaranteesAdministratorPath(location.pathname);
  const shouldCreateApplicationAfterSelection =
    isAdministratorFlow && searchParams.get('create') === 'true';
  const isCreateApplicationLoading = createApplicationStatus === GuaranteesFetchStatus.Loading;
  const isSelectorBusy = isSubmitting || isCreateApplicationLoading;
  const searchCacheMode = isAdministratorFlow ? 'administrator' : 'client';

  const cacheStorageKey = useMemo(
    () =>
      buildGuaranteesCacheStorageKey({
        mode: searchCacheMode,
        userName: userName || '',
        token,
      }),
    [searchCacheMode, userName, token]
  );

  const cachedSearchOptions = useMemo(
    () =>
      buildGuaranteesCachedSearchOptions({
        cachedSearch,
        dateFormat: COUNTERPARTY_CACHE_DATE_FORMAT,
        datePrefix: counterpartySelectorText.cachedSearchDropdownDatePrefix,
      }),
    [cachedSearch]
  );
  const normalizedSearchCodeInput = searchCodeInputValue.trim().toLowerCase();
  const visibleCachedSearchOptions = useMemo(() => {
    if (!normalizedSearchCodeInput) {
      return cachedSearchOptions;
    }

    return cachedSearchOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchCodeInput)
    );
  }, [cachedSearchOptions, normalizedSearchCodeInput]);
  const hasVisibleCachedSearchOptions = visibleCachedSearchOptions.length > 0;

  useEffect(() => {
    const parsedCache = getCachedGuaranteesSearch(cacheStorageKey);
    setCachedSearch(parsedCache);
    setSearchCodeInputValue('');

    setValue('searchCode', null, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [cacheStorageKey, setValue]);

  const handleCloseAlertModal = () => setIsModalAlertOpen(false);
  const handleCloseContragentsModal = () => setIsContragentsModalOpen(false);

  const handleCreateApplicationForContragent = async (contragentId?: number | string) => {
    const normalizedContragentId = String(contragentId ?? '').trim();

    if (!normalizedContragentId || isCreateApplicationLoading) {
      return;
    }

    try {
      const createdApplication = await dispatch(
        createGuaranteesApplication({
          contragentId: normalizedContragentId,
          flowType: 'MANUAL',
        })
      ).unwrap();

      if (!createdApplication.id) {
        setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
        setIsModalAlertOpen(true);
        return;
      }

      navigate(buildGuaranteesApplicationPath(createdApplication.id, normalizedContragentId, true));
    } catch (error) {
      setModalAlertText(getGuaranteesErrorMessage(error));
      setIsModalAlertOpen(true);
    }
  };

  const handleSearch = async ({ searchCode }: IGuaranteesSearchFormValues) => {
    const normalizedCode = normalizeGuaranteesSearchCode(searchCode);
    setContragents([]);
    setIsContragentsModalOpen(false);

    try {
      const foundContragents = isAdministratorFlow
        ? await dispatch(fetchCallCenterOperatorContragents({ code: normalizedCode })).unwrap()
        : await dispatch(fetchMyContragents({ code: normalizedCode })).unwrap();

      if (!foundContragents.length) {
        setModalAlertText(counterpartySelectorText.noContragentsFound);
        setIsModalAlertOpen(true);
        return;
      }

      const cachePayload = saveGuaranteesSearchToCache(cacheStorageKey, normalizedCode);

      if (cachePayload) {
        setCachedSearch(cachePayload);
      }

      if (foundContragents.length === 1) {
        if (shouldCreateApplicationAfterSelection) {
          await handleCreateApplicationForContragent(foundContragents[0]?.id);
          return;
        }

        navigate(buildGuaranteesCounterpartyPath(foundContragents[0]?.id, isAdministratorFlow));
        return;
      }

      setContragents(foundContragents);
      setIsContragentsModalOpen(true);
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 403) {
        setModalAlertText(counterpartySelectorText.accessDenied);
        setIsModalAlertOpen(true);
        return;
      }

      setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
      setIsModalAlertOpen(true);
    }
  };

  const handleSelectContragent = (contragentId: number) => {
    setIsContragentsModalOpen(false);

    if (shouldCreateApplicationAfterSelection) {
      void handleCreateApplicationForContragent(contragentId);
      return;
    }

    navigate(buildGuaranteesCounterpartyPath(contragentId, isAdministratorFlow));
  };

  const handleSearchFormSubmit = handleSubmit(handleSearch, () => {
    setContragents([]);
    setIsContragentsModalOpen(false);
  });

  const handleSearchCodeInput = (event: React.FormEvent<HTMLInputElement>) => {
    const newInputValue = event.currentTarget.value;
    setSearchCodeInputValue(newInputValue);
    clearErrors('searchCode');
    setValue('searchCode', newInputValue ? { label: newInputValue, value: '-1' } : null, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const handleAutocompleteOpen = () => {
    if (hasVisibleCachedSearchOptions) {
      setIsAutocompleteOpen(true);
    }
  };

  return (
    <Box sx={styles.selectorWrap}>
      <Paper elevation={3} sx={[styles.selectorCard, counterpartySelectorPaperStyle]}>
        <Typography variant="h5" gutterBottom sx={styles.selectorTitle}>
          {counterpartySelectorText.title}
        </Typography>

        <FormProvider {...formMethods}>
          <Box component="form" onSubmit={handleSearchFormSubmit} sx={styles.selectorForm}>
            <FormAutocomplete
              name="searchCode"
              options={visibleCachedSearchOptions}
              disabled={isSelectorBusy}
              shouldSaveLabel
              open={hasVisibleCachedSearchOptions && isAutocompleteOpen}
              onOpen={handleAutocompleteOpen}
              onClose={() => setIsAutocompleteOpen(false)}
              filterOptions={(options) => options}
              inputProps={{
                ...counterpartySearchInputProps,
                placeholder: counterpartySelectorText.searchPlaceholder,
                'aria-label': counterpartySelectorText.searchAriaLabel,
                onInput: handleSearchCodeInput,
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSelectorBusy}
              startIcon={isSelectorBusy ? <ButtonLoadingIcon /> : undefined}
              sx={styles.selectorSubmit}
            >
              {isSelectorBusy
                ? counterpartySelectorText.submitButtonLoading
                : counterpartySelectorText.submitButton}
            </Button>
          </Box>
        </FormProvider>
      </Paper>

      <Dialog
        open={isContragentsModalOpen}
        onClose={handleCloseContragentsModal}
        sx={styles.selectorDialog}
      >
        <DialogTitle>{counterpartySelectorText.selectionDialogTitle}</DialogTitle>
        <DialogContent>
          <List disablePadding>
            {contragents.map((contragent) => (
              <ListItemButton
                key={contragent.id}
                disabled={isCreateApplicationLoading}
                onClick={() => handleSelectContragent(contragent.id)}
              >
                <ListItemText
                  primary={contragent.displayName}
                  secondary={`${counterpartySelectorText.edrpouLabel}: ${contragent.code}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {isModalAlertOpen && (
        <ModalAlert
          isOpen={isModalAlertOpen}
          text={modalAlertText}
          onClose={handleCloseAlertModal}
        />
      )}
    </Box>
  );
};

export default GuaranteesCounterpartySelector;
